import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import classNames from 'classnames'
import { useSelector } from 'react-redux'
import { Box, Slider, InputLabel, FormControlLabel } from '@material-ui/core'
import PropTypes from 'prop-types'
import { capitalize, isNull, noop } from 'lodash'
import Select from '~/components/base/Select'
import MenuItem from '~/components/base/MenuItem'
import { TIMELINE_TIME_UNIT, timelineTimeToSeconds } from '~/Util'
import { MAX_TRANSITION_DURATION, MIN_TRANSITION_DURATION } from '~/constant'
import Input from '~/components/base/Input'
import { TRANSITIONS, HOTKEYS } from '~/enums'
import { getSelectedTransitionsByGroup, getAssets } from '~/selectors'
import { isCrossingTransition } from '~/helpers/assets/isCrossingTransition'
import { TranslationContext } from '~/contexts/TranslationContext'
import { getPermissions } from '~/actions/transition/patchTransition'
import * as PT from '~/PropTypes'
import { TransitionAsset } from '../../../models/Asset'
import { useAction } from '~/hooks'
import { historyUndo as historyUndoAction, historyRedo as historyRedoAction } from '~/actions/historyActions'
import Switch from '~/components/base/Switch'
import './TransitionDurationField.scss'


export default function TransitionDurationField(props) {
  const { t } = useContext(TranslationContext)
  const { onChangeDuration, onSwitchIsActive, onChangeType, type,
    transition, isAudioTransition, value, isActive } = props
  const MIN_TRANSITION_DURATION_SEC = timelineTimeToSeconds(MIN_TRANSITION_DURATION)
  const MAX_TRANSITION_DURATION_SEC = timelineTimeToSeconds(MAX_TRANSITION_DURATION)
  const selectedTransitions = useSelector(getSelectedTransitionsByGroup)
  const historyUndo = useAction(historyUndoAction)
  const historyRedo = useAction(historyRedoAction)
  const assets = useSelector(getAssets)

  const ref = useRef()
  const maxValueRef = useRef(0)
  const isFirstRender = useRef(true)
  const isOnChangeType = useRef(false)

  const [ inputValue, setInputValue ] = useState(null)
  const [ isInput, setIsInput ] = useState(false)
  const [ focusedInput, setFocused ] = useState(false)

  const isMixedType = selectedTransitions[type] > 0
    && selectedTransitions[type] !== selectedTransitions.count
  const isMixedDuration = !selectedTransitions?.isSameLength
  const isAllCrossing = selectedTransitions.count === selectedTransitions.crossingTransition
  const finishValue = timelineTimeToSeconds(isAudioTransition
    ? value
    : selectedTransitions.maxDuration)
  const inputFieldValue = isNull(inputValue)
    ? Number(finishValue).toFixed(2)
    : Number(inputValue).toFixed(2)
  const currentValue = isMixedDuration && isFirstRender.current ? 'mixed' : inputFieldValue

  const navigateHistory = useCallback((resetFocus, key) => {
    if (focusedInput) {
      key === HOTKEYS.UNDO ? historyUndo() : historyRedo()
    }
    setInputValue(null)
    setIsInput(false)
    if (resetFocus) {
      setFocused(false)
    }
  }, [ historyUndo, focusedInput, historyRedo ])

  useEffect(() => {
    const onKeyDown = event => {
      if (event.ctrlKey && event.key === 'z') {
        navigateHistory(false, HOTKEYS.UNDO)
      }
    }
    const onClick = event => {
      if (event.target.closest(`#${HOTKEYS.UNDO}`)) {
        navigateHistory(true, HOTKEYS.UNDO)
      } else if (event.target.closest(`#${HOTKEYS.REDO}`)) {
        navigateHistory(true, HOTKEYS.REDO)
      } else if (event.target !== ref.current?.firstElementChild) {
        setFocused(false)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('click', onClick)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('click', onClick)
    }
  }, [ navigateHistory, focusedInput ])

  useEffect(() => {
    if (selectedTransitions.count === 1) {
      const { leftVideoAssetId, rightVideoAssetId, duration, type, id } = transition ?? {}
      if (Object.values(TRANSITIONS).includes(type)) {
        const transitionParams = new Map()
        transitionParams.set(transition.id, {
          leftAssetId: leftVideoAssetId,
          rightAssetId: rightVideoAssetId,
          prevDuration: duration,
          diff: MAX_TRANSITION_DURATION - transition.duration,
          transitionType: type,
          transitionId: id,
          transitionAsset: transition,
        })
        const { maxValue } = getPermissions({ layer: { assets } }, transitionParams)
        maxValueRef.current = timelineTimeToSeconds(maxValue)
      }
    } else if (isAudioTransition) {
      maxValueRef.current = timelineTimeToSeconds(Math.min(MAX_TRANSITION_DURATION,
        transition.duration))
      if (type === TRANSITIONS.FADEIN) {
        if (transition.fadeOutIsActive) {
          maxValueRef.current = timelineTimeToSeconds(Math.max(MIN_TRANSITION_DURATION,
            transition.duration - transition.fadeOutDuration))
        }
      } else if (type === TRANSITIONS.FADEOUT) {
        if (transition.fadeInIsActive) {
          maxValueRef.current = timelineTimeToSeconds(Math.max(MIN_TRANSITION_DURATION,
            transition.duration - transition.fadeInDuration))
        }
      }
    }
    return () => {
      maxValueRef.current = 0
    }
  }, [ transition, assets, selectedTransitions, value,
    isAudioTransition, type ])

  const options = useMemo(() => {
    const isCrossingType = isCrossingTransition(type)
    return Object.keys(TRANSITIONS)
      .filter(key => isCrossingType
        ? isCrossingTransition(TRANSITIONS[key])
        : TRANSITIONS[key] === type)
      .map(key => (
        <MenuItem
          value={TRANSITIONS[key]}
          key={TRANSITIONS[key]}
        >
          {t(TransitionAsset.getTranslationKey(TRANSITIONS[key]))}
        </MenuItem>
      ))
  }, [ type, t ])

  const parseValue = useCallback(async (val, resetInputValue = false) => {
    isFirstRender.current = false
    let newValue = parseFloat(val)
    if (isNaN(newValue)) {
      ref.current.value = finishValue
    } else {
      if (newValue > MAX_TRANSITION_DURATION_SEC) {
        newValue = MAX_TRANSITION_DURATION_SEC
      }
      if (newValue < MIN_TRANSITION_DURATION_SEC) {
        newValue = MIN_TRANSITION_DURATION_SEC
      }
      if (selectedTransitions[type] || isAudioTransition) {
        await onChangeDuration(Number(newValue) * TIMELINE_TIME_UNIT)
      }
      setInputValue(Number(newValue.toFixed(2)))
    }
    if (resetInputValue) {
      setInputValue(null)
    }
    setIsInput(false)
  }, [
    selectedTransitions,
    onChangeDuration,
    setInputValue,
    setIsInput,
    finishValue,
    type,
    isAudioTransition,
    MIN_TRANSITION_DURATION_SEC,
    MAX_TRANSITION_DURATION_SEC,
  ])


  const onKeyDown = useCallback(e => {
    if (e.key === 'Enter') {
      e.preventDefault()
      const isReset = selectedTransitions.count === 1 || isAudioTransition
      parseValue(e.target.value, isReset)
    }
  }, [ parseValue, selectedTransitions.count, isAudioTransition ])

  const onBlur = useCallback(e => {
    e.preventDefault()
    parseValue(e.target.value)
  }, [ parseValue ])

  const onSliderChange = useCallback((e, value) => {
    isFirstRender.current = false
    e.preventDefault()
    e.stopPropagation()
    let newValue = value
    if (isNaN(parseFloat(value))) {
      newValue = finishValue
    }
    const isMax = newValue > maxValueRef.current
    let returnValue = value
    if (isMax && maxValueRef.current) {
      returnValue = maxValueRef.current
    }
    setInputValue(returnValue)
    return returnValue
  }, [ setInputValue, finishValue ])


  const onChangeCommitted = useCallback(async (e, value) => {
    e.preventDefault()
    e.stopPropagation()
    const sliderValue = onSliderChange(e, value)
    await parseValue(sliderValue, false)
  }, [ onSliderChange, parseValue ])

  const onChangeInput = useCallback(e => {
    isFirstRender.current = false
    setIsInput(true)
    setInputValue(e.target.value)
    setFocused(true)
  }, [ setInputValue, setIsInput ])

  const onFocus = e => {
    e.preventDefault()
    e.stopPropagation()
    e.target.select()
    setFocused(true)
  }

  const onChangeTransitionType = useCallback(e => {
    isOnChangeType.current = true
    onChangeType(e.target.value)
  }, [ onChangeType ])

  const onSwitch = useCallback(async e => {
    const on = !!e.target.checked
    const off = !on
    await parseValue(on ? Math.min(maxValueRef.current, currentValue) : '3.00', true)
    if (off) {
      onSwitchIsActive(false)
    }
  }, [ currentValue, parseValue, onSwitchIsActive ])

  const audioTransitionLabel = TRANSITIONS.FADEIN === type
    ? t('AUDIO_SETTINGS_FORM_LABEL_FADE_IN')
    : t('AUDIO_SETTINGS_FORM_LABEL_FADE_OUT')

  return (
    <div className="asset-transition-field">
      {!isAudioTransition && (
        <Box display="flex" alignItems="center" marginBottom="30px">
          <Box className="asset-transition-field__title">
            {t('TRANSITION_FORM_LABEL_TYPE')}
          </Box>
          <Box position="relative">
            <InputLabel id="select-transition" className="asset-transition-field__label">
              {isMixedType && !isOnChangeType.current && isAllCrossing ? 'Mixed' : ''}
            </InputLabel>
            <Select
              labelId="select-transition"
              value={isMixedType && !isOnChangeType.current ? 'mixed' : type}
              onChange={onChangeTransitionType}
              className={classNames('asset-transition-field__select', {
                mixed: (isMixedType && !isAllCrossing) || options.length === 1,
              })}
              disabled={(isMixedType && !isAllCrossing) || options.length === 1}
              rowHeight={32}
              dropdownClassName="asset-transition-field__drop-down"
            >
              {isMixedType && !isAllCrossing
                ? <MenuItem data-id="" value="mixed">{capitalize('mixed')}</MenuItem>
                : options}
            </Select>
          </Box>
        </Box>
      )}
      <Box display="flex" alignItems="center" justifyContent={isAudioTransition ? 'space-between' : 'flex-start'} gridGap="2px">
        <div className="asset-transition-field__title">
          {isAudioTransition && (
            <FormControlLabel
              control={(
                <div className="asset-transition-field-switcher__switch">
                  <Switch
                    onChange={onSwitch}
                    checked={isActive}
                  />
                </div>
              )}
            />
          )}
          {isAudioTransition ? audioTransitionLabel : t('TRANSITION_FORM_LABEL_DURATION')}
        </div>
        <Box
          width="290px"
          display="flex"
          alignItems="center"
          gridGap="12px"
          disabled={!isActive}
          style={{ opacity: !isActive ? 0.5 : 1, cursor: !isActive ? 'not-allowed' : 'default', pointerEvents: !isActive ? 'none' : 'all' }}
        >
          <Slider
            aria-label="Small steps"
            className="asset-transition-field__slider"
            defaultValue={finishValue}
            value={isInput ? finishValue : (inputValue || finishValue)}
            step={0.1}
            scale={x => x * 0.25}
            min={MIN_TRANSITION_DURATION_SEC}
            max={MAX_TRANSITION_DURATION_SEC}
            color="var(--text-color)"
            onChange={onSliderChange}
            onChangeCommitted={onChangeCommitted}
          />
          <Input
            ref={ref}
            onFocus={onFocus}
            onChange={onChangeInput}
            value={currentValue}
            onKeyDown={onKeyDown}
            onBlur={onBlur}
            disabled={!isActive}
            className="asset-transition-field__input"
          />
        </Box>
      </Box>
    </div>
  )
}

TransitionDurationField.defaultProps = {
  isAudioTransition: false,
  isActive: true,
  onSwitchIsActive: noop,
}

TransitionDurationField.propTypes = {
  onChangeDuration: PropTypes.func.isRequired,
  onChangeType: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  transition: PT.TransitionAsset.isRequired,
  value: PropTypes.number.isRequired,
  isAudioTransition: PropTypes.bool,
  isActive: PropTypes.bool,
  onSwitchIsActive: PropTypes.func,
}
